// src/stores/dataStore.js
import { create } from 'zustand'

const INVESTIGATION_TTL_MS = 30 * 60 * 1000

export const useDataStore = create((set, get) => ({
  speed: { downloadBps: 0, uploadBps: 0, downloadHuman: '0 B/s', uploadHuman: '0 B/s' },
  speedHistory: [],
  connections: [],
  retainedConnections: {},
  aiExplanations: {},
  lastConnectionsUpdate: null,
  metrics: {
    cpu: { usage: 0, cores: 0, load: 0, coreCount: 0, loadAvg1m: '0', model: '' },
    memory: { percentUsed: 0, usedGB: '0', totalGB: '0' },
  },
  alerts: [],
  activeTab: 'connections',
  selectedConnectionId: null,
  isLoading: true,
  searchQuery: '',
  soundEnabled: true,

  setSpeed: (speed) => set((state) => {
    const prev = state.speedHistory
    const next = prev.length >= 60
      ? [...prev.slice(1), { downloadBps: speed.downloadBps, uploadBps: speed.uploadBps, t: Date.now() }]
      : [...prev,          { downloadBps: speed.downloadBps, uploadBps: speed.uploadBps, t: Date.now() }]
    return { speed, speedHistory: next }
  }),

  setConnections: (connections) => set((state) => {
    const now = Date.now()
    const liveById = new Map(connections.map(conn => [conn.id, conn]))
    const retainedConnections = {}
    const aiExplanations = { ...state.aiExplanations }

    for (const [id, snapshot] of Object.entries(state.retainedConnections)) {
      const ai = state.aiExplanations[id]
      const isLoading = ai?.status === 'loading'
      const isRecent = now - (snapshot.retainedAt || now) < INVESTIGATION_TTL_MS
      const shouldKeep = isLoading || (isRecent && (ai?.status === 'done' || ai?.status === 'error'))

      if (shouldKeep) {
        retainedConnections[id] = {
          ...(liveById.get(id) || snapshot),
          retainedAt: snapshot.retainedAt || now,
        }
      } else if (!liveById.has(id)) {
        delete aiExplanations[id]
      }
    }

    return {
      connections,
      retainedConnections,
      aiExplanations,
      lastConnectionsUpdate: now,
      isLoading: false,
    }
  }),

  setMetrics: (metrics) => set({ metrics }),

  addAlert: (alert) => set((state) => ({
    alerts: [alert, ...state.alerts].slice(0, 100),
  })),

  dismissAlert: (alertId) => set((state) => ({
    alerts: state.alerts.filter(a => a.id !== alertId),
  })),

  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedConnection: (id) => set({ selectedConnectionId: id }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setSoundEnabled: (val) => set({ soundEnabled: val }),

  retainConnection: (conn) => set((state) => {
    if (!conn?.id) return {}
    return {
      retainedConnections: {
        ...state.retainedConnections,
        [conn.id]: {
          ...conn,
          retainedAt: state.retainedConnections[conn.id]?.retainedAt || Date.now(),
        },
      },
    }
  }),

  releaseRetainedConnection: (id) => set((state) => {
    const retainedConnections = { ...state.retainedConnections }
    const aiExplanations = { ...state.aiExplanations }
    delete retainedConnections[id]
    delete aiExplanations[id]
    return { retainedConnections, aiExplanations }
  }),

  setAIExplanation: (id, patch) => set((state) => {
    if (!id) return {}
    return {
      aiExplanations: {
        ...state.aiExplanations,
        [id]: {
          status: 'idle',
          text: '',
          fullText: '',
          aiResult: null,
          ...(state.aiExplanations[id] || {}),
          ...patch,
          updatedAt: Date.now(),
        },
      },
    }
  }),

  getVisibleConnections: () => {
    const { connections, retainedConnections } = get()
    const activeIds = new Set(connections.map(conn => conn.id))
    const retained = Object.values(retainedConnections)
      .filter(conn => conn?.id && !activeIds.has(conn.id))
      .map(conn => ({ ...conn, isRetained: true }))

    return [...connections, ...retained]
  },

  getConnectionsByApp: () => {
    const connections = get().getVisibleConnections()
    const byApp = {}
    for (const conn of connections) {
      const k = conn.appName
      if (!byApp[k]) byApp[k] = { appName: k, connections: [], hasWarning: false }
      byApp[k].connections.push(conn)
      if (conn.riskLevel === 'warning' || conn.riskLevel === 'critical') byApp[k].hasWarning = true
    }
    return Object.values(byApp).sort((a, b) => b.connections.length - a.connections.length)
  },

  getActiveAlerts: () => get().alerts.filter(a => !a.dismissed),
}))
